<template>
  <div class="tracking-app" lang="en">
    <header class="tracking-nav">
      <div class="tracking-brand"><b>Phoenix</b></div>
      <nav>
        <router-link class="active" to="/">Track a shipment</router-link>
        <router-link to="/quota">Request a quote</router-link>
      </nav>
      <div class="nav-right-slot">
        <button class="language-switch" type="button" @click="$emit('switch-language')" aria-label="Switch to Chinese page">中文</button>
      </div>
    </header>

    <section class="tracking-hero">
      <div class="hero-content">
        <small class="hero-brand">PHOENIX LOGISTICS</small>
        <h1>Know where your cargo is</h1>
        <p class="hero-sub">Enter your tracking number to see the latest sea freight, air freight or Air&&EXpress updates.</p>
        <div class="tracking-search">
          <input
            v-model.trim="query"
            @keyup.enter="search"
            placeholder="Enter a tracking number, e.g. SEAE000000000 / AIRE000000000"
          >
          <button :disabled="loading" @click="search">
            <span v-if="loading" class="track-spinner"></span>{{ loading ? 'Searching...' : 'Track shipment' }}
          </button>
        </div>
        <div v-if="loading" class="query-loading">Looking up your shipment...</div>
        <div v-if="error" class="query-error">{{ error }}</div>
      </div>
    </section>

    <main v-if="shipment" class="tracking-main">
      <!-- Template A: Shipments standard freight template (rendered when querying standard Shipments records: 100% original) -->
      <section v-if="!shipment.isExpress && shipment.source !== 'EXPRESS'" class="shipment-card">
        <div class="shipment-head">
          <div class="shipment-title">
            <div class="transport-icon">{{ shipment.transportMode === 'SEA' ? '⚓' : '✈' }}</div>
            <div>
              <h2>{{ shipment.trackingNo }}</h2>
              <p class="route-text">{{ shipment.origin }} → {{ shipment.destination }}</p>
            </div>
          </div>
          <span class="mode-pill">{{ shipment.transportMode === 'SEA' ? 'Sea freight' : 'Air freight' }}</span>
        </div>

        <section v-if="hasShipmentDetails" class="cargo-summary">
          <h3>Shipment details</h3>
          <div class="cargo-grid">
            <div v-if="hasValue(shipment.cargoName)" class="cargo-field cargo-name full-row">
              <span class="field-label">Cargo name</span><b class="field-value">{{ shipment.cargoName }}</b>
              <button type="button" class="view-cargo" @click="showCargoModal = true">View all</button>
            </div>
            <div v-if="hasValue(shipment.supplierName)" class="cargo-field full-row">
              <span class="field-label">Supplier</span><b class="field-value">{{ shipment.supplierName }}</b>
            </div>
            <div v-if="hasValue(shipment.weight)" class="cargo-field">
              <span class="field-label">Weight</span><b class="field-value">{{ shipment.weight }}</b>
            </div>
            <div v-if="hasValue(shipment.volume)" class="cargo-field">
              <span class="field-label">Volume</span><b class="field-value">{{ shipment.volume }} CBM</b>
            </div>
            <div v-if="hasValue(shipment.packageQuantity)" class="cargo-field">
              <span class="field-label">Packages</span><b class="field-value">{{ packageCount(shipment.packageQuantity) }}</b>
            </div>
          </div>
        </section>

        <div class="timeline">
          <article v-for="event in shipment.events" :key="event.nodeCode" class="timeline-event">
            <span class="timeline-dot" :class="{ done: event.statusCode }"></span>
            <div class="timeline-content">
              <div class="event-title">
                <b>{{ eventName(event) }}</b>
                <span v-if="event.statusCode && eventStatus(event)">{{ eventStatus(event) }}</span>
              </div>
              <small v-if="event.occurredAt" class="event-time">Last updated: {{ formatDate(event.occurredAt) }}</small>
              <div v-if="event.fields && event.fields.length" class="event-fields">
                <div v-for="field in event.fields" v-if="showField(event, field)" :key="field.label">
                  <span>{{ fieldName(field.label) }}</span><b>{{ field.value }}</b>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Template B: Air&&EXpress dedicated template (Shipment details strictly matched, horizontal stepper, bottom trajectory) -->
      <section v-else class="shipment-card express-card">
        <div class="shipment-head">
          <div class="shipment-title">
            <div class="transport-icon">✈</div>
            <div>
              <h2 class="shipment-no-title">{{ shipment.trackingNo || shipment.systemNo }}</h2>
              <p class="route-text">{{ shipment.origin || 'Origin' }} → {{ shipment.destination || 'Destination' }}</p>
            </div>
          </div>
          <span class="mode-pill">{{ carrierName(shipment.carrier) }}</span>
        </div>

        <!-- Shipment details (Strictly matching Shipments, no track number) -->
        <section v-if="hasShipmentDetails" class="cargo-summary">
          <h3>Shipment details</h3>
          <div class="cargo-grid">
            <div v-if="hasValue(shipment.cargoName)" class="cargo-field cargo-name full-row">
              <span class="field-label">Cargo name</span><b class="field-value">{{ shipment.cargoName }}</b>
              <button type="button" class="view-cargo" @click="showCargoModal = true">View all</button>
            </div>
            <div v-if="hasValue(shipment.supplierName)" class="cargo-field full-row">
              <span class="field-label">Supplier</span><b class="field-value">{{ shipment.supplierName }}</b>
            </div>
            <div v-if="hasValue(shipment.weight)" class="cargo-field">
              <span class="field-label">Weight</span><b class="field-value">{{ shipment.weight }}</b>
            </div>
            <div v-if="hasValue(shipment.volume)" class="cargo-field">
              <span class="field-label">Volume</span><b class="field-value">{{ shipment.volume }} CBM</b>
            </div>
            <div v-if="hasValue(shipment.packageQuantity)" class="cargo-field">
              <span class="field-label">Packages</span><b class="field-value">{{ packageCount(shipment.packageQuantity) }}</b>
            </div>
          </div>
        </section>

        <!-- Horizontal Milestone Steps (only selected nodes are displayed, large clear timestamps) -->
        <section v-if="selectedMilestoneEvents.length" class="milestone-stepper-wrap">
          <div class="horizontal-stepper">
            <div
              v-for="(ev, idx) in selectedMilestoneEvents"
              :key="ev.nodeCode || idx"
              class="stepper-item"
              :class="{
                'step-done': ev.statusCode === 'COMPLETED',
                'step-active': ev.statusCode === 'PROCESSING',
                'step-wait': !ev.statusCode || ev.statusCode === 'PENDING'
              }"
            >
              <div class="line-left" v-if="idx > 0"></div>
              <div class="step-circle">
                <span v-if="ev.statusCode === 'COMPLETED'">✓</span>
                <span v-else-if="ev.statusCode === 'PROCESSING'">●</span>
                <span v-else>{{ idx + 1 }}</span>
              </div>
              <div class="line-right" v-if="idx < selectedMilestoneEvents.length - 1"></div>
              <div class="step-label-box">
                <div class="step-name">{{ expressNodeNameEn(ev.nodeCode) }}</div>
                <div class="step-status">{{ expressNodeStatusEn(ev.statusCode) }}</div>
                <div v-if="ev.occurredAt" class="step-time">{{ formatDate(ev.occurredAt) }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Bottom: Real-time Crawled Trajectory (large clear timestamps) -->
        <div class="timeline-section">
          <div class="section-head">
            <h3>Real-time Tracking Trajectory</h3>
            <small v-if="shipment.crawlerEvents && shipment.crawlerEvents.length">
              {{ shipment.crawlerEvents.length }} event(s) recorded
            </small>
          </div>

          <!-- Trajectory Stream -->
          <div v-if="shipment.crawlerEvents && shipment.crawlerEvents.length" class="timeline">
            <article
              v-for="(ce, idx) in shipment.crawlerEvents"
              :key="idx"
              class="timeline-event"
              :class="{ 'latest-event': idx === 0 }"
            >
              <span class="timeline-dot" :class="{ done: idx === 0 }"></span>
              <div class="timeline-content">
                <div class="event-title">
                  <b>{{ ce.status || ce.description || 'Status update' }}</b>
                  <span v-if="ce.location" class="location-badge">{{ ce.location }}</span>
                  <span v-if="idx === 0" class="tag-latest">Latest</span>
                </div>
                <div class="event-time">Event time: {{ ce.time }}</div>
                <p v-if="ce.description && ce.description !== ce.status" class="event-desc">{{ ce.description }}</p>
              </div>
            </article>
          </div>

          <div v-else class="empty-trajectory-box">
            <p>No live tracking events recorded yet. Updates will appear once processed by the carrier.</p>
          </div>
        </div>
      </section>
    </main>

    <section v-else class="query-placeholder">
      <div>⌕</div>
      <p>Enter a tracking number to view your shipment updates</p>
    </section>

    <div v-if="showCargoModal" class="cargo-modal-backdrop" @click.self="showCargoModal = false">
      <section class="cargo-modal" role="dialog" aria-modal="true">
        <header>
          <h2>Full cargo name</h2>
          <button type="button" aria-label="Close" @click="showCargoModal = false">×</button>
        </header>
        <p>{{ shipment.cargoName }}</p>
      </section>
    </div>

    <footer>© 2026 Phoenix Logistics · Clear updates for every shipment</footer>
  </div>
</template>

<script>
import mixin from './tracking-page-mixin'

const nodeNames = {
  ORDER_ACCEPTED: 'Order received',
  BOOKING: 'Booking',
  RELEASE: 'Space release',
  CONTAINER_LOADING: 'Container loading',
  CUSTOMS_DECLARATION: 'Customs declaration',
  DEPARTURE: 'Vessel departure',
  PRE_CLEARANCE: 'Pre-clearance',
  ARRIVAL_PORT: 'Arrival at port',
  CONTAINER_UNLOADING: 'Container unloading',
  DELIVERY: 'Delivery',
  EMPTY_RETURN: 'Empty container return',
  WAREHOUSE_IN: 'Bonded warehouse receipt',
  TAKE_OFF: 'Flight departure',
  ARRIVAL: 'Arrival',
  PICKUP_DELIVERY: 'Pickup & delivery',
  SHIPMENT: 'Shipment dispatched',
  CUSTOMS: 'Customs clearance',
  CARRIER_TRACKING: 'Carrier trajectory',
  CLEARANCE: 'Destination clearance'
}

const statusNames = {
  ACCEPTED: 'Order accepted',
  RECEIVED: 'Cargo received',
  NOT_RECEIVED: 'Cargo not received',
  BOOKED: 'Booking confirmed',
  NOT_BOOKED: 'Not booked',
  RELEASED: 'Space released',
  NOT_RELEASED: 'Space not released',
  LOADED: 'Container loaded',
  NOT_LOADED: 'Container not loaded',
  DECLARED: 'Customs declared',
  NOT_DECLARED: 'Customs not declared',
  DEPARTED: 'Vessel departed',
  NOT_DEPARTED: 'Vessel not departed',
  CLEARING: 'Customs clearance in progress',
  CLEARED: 'Customs clearance completed',
  ARRIVED: 'Arrived at port',
  NOT_ARRIVED: 'Not yet arrived at port',
  UNPACKING: 'Container unloading in progress',
  UNPACKED: 'Container unloading completed',
  WAITING_DELIVERY_YES: 'Awaiting delivery',
  WAITING_DELIVERY_NO: 'Not awaiting delivery',
  RETURNED: 'Empty container returned',
  NOT_RETURNED: 'Empty container not returned',
  AIR_RECEIVED: 'Cargo received',
  AIR_NOT_RECEIVED: 'Cargo not received',
  AIR_BOOKED: 'Booking confirmed',
  AIR_NOT_BOOKED: 'Not booked',
  AIR_RELEASED: 'Space released',
  AIR_NOT_RELEASED: 'Space not released',
  WAREHOUSE_IN: 'Received at warehouse',
  NOT_WAREHOUSE_IN: 'Not received at warehouse',
  DECLARATION_PROCESSING: 'Customs declaration in progress',
  DECLARATION_SUCCESS: 'Customs declaration completed',
  TAKEN_OFF: 'Flight departed',
  NOT_TAKEN_OFF: 'Flight not departed',
  AIR_CLEARING: 'Customs clearance in progress',
  AIR_CLEARED: 'Customs clearance completed',
  AIR_ARRIVED: 'Arrived',
  AIR_NOT_ARRIVED: 'Not yet arrived',
  PICKED_UP: 'Collected',
  NOT_PICKED_UP: 'Not collected',
  DELIVERING: 'Out for delivery',
  DELIVERED: 'Delivered',
  UNDER_DECLARATION: 'Customs declaration in progress',
  DECLARATION_FAILED: 'Customs declaration exception',
  IN_TRANSIT: 'In transit',
  PENDING: 'Pending',
  PROCESSING: 'In progress',
  COMPLETED: 'Completed'
}

const fieldNames = {
  '船名船次': 'Vessel / voyage',
  '航班号': 'Flight number',
  '派送时间': 'Delivery time',
  '地址': 'Address',
  '营业时间': 'Opening time',
  '有无叉车': 'Forklift available',
  '柜子朝向': 'Container orientation',
  '正在派送中': 'Out for delivery',
  '报关单号': 'Customs declaration number',
  '最迟还空柜时间': 'Latest empty-container return time',
  '实际还空柜时间': 'Actual empty-container return time',
  '清关状态': 'Clearance status'
}

export default {
  name: 'TrackingEnglishPage',
  mixins: [mixin],
  computed: {
    emptyTrackingMessage: () => 'Please enter a tracking number.',
    longTrackingMessage: () => 'The tracking number is too long. Please check and try again.',
    notFoundMessage: () => 'We could not find that shipment. Please check the tracking number and try again.',
    unavailableMessage: () => 'Tracking is temporarily unavailable. Please try again shortly.',
    selectedMilestoneEvents () {
      if (!this.shipment || !this.shipment.events) return []
      return this.shipment.events.filter(e => e.isEnabled !== 'N' && e.isEnabled !== false)
    }
  },
  methods: {
    eventName: e => nodeNames[e.nodeCode] || e.name,
    eventStatus: e => (e.statusCode && statusNames[e.statusCode]) || (e.statusLabel && e.statusLabel !== '待维护状态' ? e.statusLabel : ''),
    fieldName: l => fieldNames[l] || l,
    expressNodeNameEn (code) {
      const map = {
        SHIPMENT: 'Shipment dispatched',
        CUSTOMS: 'Customs clearance',
        CARRIER_TRACKING: 'Carrier trajectory',
        CLEARANCE: 'Destination clearance',
        DELIVERY: 'Delivery'
      }
      return map[code] || code
    },
    expressNodeStatusEn (statusCode) {
      if (statusCode === 'COMPLETED') return 'Completed'
      if (statusCode === 'PROCESSING') return 'In progress'
      return 'Pending update'
    },
    formatDate (value) {
      if (!value) return ''
      const m = String(value).match(/^(\d{4})[-/](\d{2})[-/](\d{2})(?:[ T](\d{2}:\d{2}))?/)
      return m ? `${m[3]} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1]} ${m[1]}${m[4] ? ` ${m[4]}` : ''}` : value
    },
    carrierName (c) {
      return {
        CHINA_SOUTHERN: 'China Southern Airlines (CSAir)',
        DHL: 'DHL Express',
        QIANQIU: 'BaslExpress'
      }[c] || c
    }
  }
}
</script>

<style scoped>
.hero-brand {
  font-size: 14px;
  font-weight: 800;
  color: #1e40af;
  letter-spacing: 1.5px;
}
.hero-sub {
  font-size: 16px;
  color: #1e293b;
  font-weight: 600;
}
.shipment-head {
  padding: 24px 32px;
}
.shipment-no-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}
.route-text {
  font-size: 16px;
  color: #334155;
  font-weight: 700;
  margin-top: 6px;
}
.mode-pill {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1.5px solid #bfdbfe;
  font-size: 15px;
  font-weight: 800;
  padding: 8px 18px;
  border-radius: 20px;
}
.cargo-summary {
  padding: 24px 32px;
}
.cargo-summary h3 {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 16px;
}
.cargo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.cargo-field {
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-label {
  font-size: 14px;
  color: #475569;
  font-weight: 700;
}
.field-value {
  font-size: 17px;
  color: #0f172a;
  font-weight: 800;
}
.view-cargo {
  font-size: 14px;
  font-weight: 700;
  color: #2563eb;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 4px;
  border: none;
  background: none;
  padding: 0;
}
.milestone-stepper-wrap {
  padding: 28px 32px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}
.horizontal-stepper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  width: 100%;
}
.stepper-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  text-align: center;
}
.step-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  z-index: 2;
  border: 2px solid #cbd5e1;
  transition: all 0.3s;
}
.step-done .step-circle {
  background: #10b981;
  color: #fff;
  border-color: #059669;
}
.step-active .step-circle {
  background: #2563eb;
  color: #fff;
  border-color: #1d4ed8;
  box-shadow: 0 0 0 5px #bfdbfe;
}
.line-left, .line-right {
  position: absolute;
  top: 19px;
  height: 3px;
  background: #cbd5e1;
  z-index: 1;
}
.line-left {
  left: 0;
  right: 50%;
}
.line-right {
  left: 50%;
  right: 0;
}
.step-done .line-left, .step-done .line-right {
  background: #10b981;
}
.step-label-box {
  margin-top: 12px;
}
.step-name {
  font-size: 16px;
  color: #0f172a;
  font-weight: 800;
  display: block;
}
.step-status {
  font-size: 14px;
  color: #475569;
  font-weight: 700;
  margin-top: 4px;
  display: block;
}
.step-done .step-status {
  color: #059669;
  font-weight: 800;
}
.step-active .step-status {
  color: #2563eb;
  font-weight: 800;
}
.step-time {
  font-size: 14px;
  color: #1e293b;
  font-weight: 700;
  margin-top: 5px;
  display: block;
}
.timeline-section {
  padding: 28px 32px;
}
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}
.section-head h3 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
  font-weight: 800;
}
.section-head small {
  color: #475569;
  font-size: 14px;
  font-weight: 700;
}
.event-title b {
  font-size: 17px;
  color: #0f172a;
  font-weight: 800;
}
.latest-event .timeline-dot {
  width: 16px;
  height: 16px;
  border-color: #10b981;
  background: #10b981;
  box-shadow: 0 0 0 5px #a7f3d0;
}
.location-badge {
  background: #dbeafe;
  color: #1e40af;
  font-size: 13px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 5px;
  margin-left: 10px;
}
.tag-latest {
  background: #dcfce7;
  color: #15803d;
  font-size: 13px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 5px;
  border: 1px solid #86efac;
  margin-left: 8px;
}
.event-time {
  color: #1e293b;
  font-size: 15px;
  font-weight: 700;
  margin-top: 6px;
  display: block;
}
.event-desc {
  margin: 8px 0 0;
  color: #1e293b;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.7;
}
.empty-trajectory-box {
  padding: 32px;
  text-align: center;
  color: #475569;
  font-size: 15px;
  font-weight: 600;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}
</style>
